export const cloneDeepJSON = <T>(source: T) => {
  if (source) {
    return JSON.parse(JSON.stringify(source)) as unknown as T;
  }
  return source;
};

export const isEqual = (a, b): boolean => {
  function getType(obj) {
    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  }

  function areArraysEqual() {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }

  function areObjectsEqual() {
    if (Object.keys(a).length !== Object.keys(b).length) return false;
    for (const key in a) {
      if (Object.prototype.hasOwnProperty.call(a, key)) {
        if (!isEqual(a[key], b[key])) return false;
      }
    }
    return true;
  }

  function areFunctionsEqual() {
    return a.toString() === b.toString();
  }

  function arePrimativesEqual() {
    return a === b;
  }

  const type = getType(a);
  if (type !== getType(a)) return false;

  if (type === 'array') return areArraysEqual();
  if (type === 'object') return areObjectsEqual();
  if (type === 'function') return areFunctionsEqual();
  return arePrimativesEqual();
};

export const uniqBy = <T, P extends keyof T>(arr: T[], predicate: P | ((item: T) => unknown)) => {
  if (!Array.isArray(arr)) {
    return [] as unknown as T[];
  }

  const cb = typeof predicate === 'function' ? predicate : (o: T) => o[predicate];

  const pickedObjects = arr
    .filter(item => item)
    .reduce((map, item) => {
      const key = cb(item);

      if (!key) {
        return map;
      }

      return map.has(key) ? map : map.set(key, item);
    }, new Map<unknown, T>())
    .values();

  return [...pickedObjects];
};

export type GenericStringRecordType<T extends Record<string, string | boolean>> = {
  [K in keyof T as K]: `${T[K]}`;
};

/**
 * Converts type <T> into Record<string, string>
 * @param object
 */
export function toStringRecord<T extends Record<string, string | boolean>>(object: T) {
  return Object.entries(object).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: `${value}`,
    }),
    {} as GenericStringRecordType<T>,
  );
}
